// src/pages/MesRendezvous.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import 'moment/locale/fr';
import 'moment/locale/en-gb';
import moment from 'moment-timezone';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Modal from 'react-modal';
import './MesRendezvous.css';

// ⬇️ Utilitaires d'affichage Montréal
import { formatInMontreal } from '../utils/tz';
// ⬇️ Utilisé pour construire des Date “figées” à l’heure Montréal
import { formatInTimeZone } from 'date-fns-tz';
import { useLanguage } from '../i18n/LanguageContext';
import { getApiBase, getAuthServerBase } from '../utils/runtime';
import { formatCareLabel } from '../care/labels';

const AUTH_SERVER = getAuthServerBase();
const API_BASE = getApiBase();

moment.locale('fr');
const localizer = momentLocalizer(moment);
Modal.setAppElement('#root');

/**
 * Convertit un ISO (UTC) en "YYYY-MM-DD HH:mm:ss" Montréal,
 * puis en Date locale "figée" (RBC affichera la même heure quelle que soit la machine).
 */
const toLocalDateFromMontreal = (isoOrLocal) => {
  if (!isoOrLocal) return new Date();
  try {
    // Si on nous donne un "YYYY-MM-DD HH:mm:ss" (sans Z), formatInTimeZone le parse en Date locale du navigateur.
    // Mais on lui demande de formatter en "America/Toronto" → on récupère la "vraie" heure Montréal
    // puis on construit une Date() locale “figée” avec cette heure.
    const s = formatInTimeZone(isoOrLocal, 'America/Toronto', 'yyyy-MM-dd HH:mm:ss');
    return new Date(s.replace(' ', 'T')); // <-- Date locale "figée" à l'heure Montréal
  } catch (e) {
    console.warn('⚠️ toLocalDateFromMontreal: input non standard, fallback new Date', isoOrLocal, e);
    return new Date(isoOrLocal);
  }
};

function MesRendezvous() {
  const { isEnglish } = useLanguage();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRdv, setSelectedRdv] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState(Views.MONTH);
  const [notesVisible, setNotesVisible] = useState({});
  const [reloadAppointments, setReloadAppointments] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    moment.locale(isEnglish ? 'en-gb' : 'fr');
  }, [isEnglish]);

  useEffect(() => {
    let email = null;

    fetch(`${AUTH_SERVER}/me`, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error(isEnglish ? 'Not authenticated' : 'Non authentifie');
        return res.json();
      })
      .then(userInfo => {
        email = userInfo.email;
        return fetch(`${API_BASE}/users/email/${email}`);
      })
      .then(res => res.json())
      .then(user => {
        const userId = user.user_id;
        return fetch(`${API_BASE}/patients/${userId}/appointments`);
      })
      .then(res => res.json())
      .then(async (data) => {
        const activeAppointments = data.filter(r => r.status !== 'cancelled');

        const appointmentsWithNotes = await Promise.all(
          activeAppointments.map(async (rdv) => {
            try {
              const notesRes = await fetch(
                `${API_BASE}/appointments/${rdv.appointment_id}/notes`
              );
              const notes = await notesRes.json();
              return { ...rdv, notes };
            } catch (err) {
              console.error('Error loading appointment notes:', err);
              return { ...rdv, notes: [] };
            }
          })
        );

        setAppointments(appointmentsWithNotes);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading appointments:', err);
        setLoading(false);
      });
  }, [reloadAppointments, isEnglish]);

  const handleAnnuler = async (appointment_id) => {
    const confirm = window.confirm(isEnglish ? 'Are you sure you want to cancel this appointment?' : 'Etes-vous sur de vouloir annuler ce rendez-vous ?');
    if (!confirm) return;

    try {
      const res = await fetch(
        `${API_BASE}/appointments/${appointment_id}/cancel`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        }
      );

      if (res.ok) {
        setReloadAppointments(prev => !prev);
        alert(isEnglish ? 'Your appointment has been cancelled.' : 'Rendez-vous annule avec succes.');
        setModalOpen(false);
      } else {
        alert(isEnglish ? 'There was a problem cancelling this appointment.' : 'Erreur lors de l annulation.');
      }
    } catch (err) {
      console.error(err);
      alert(isEnglish ? 'Network error' : 'Erreur reseau');
    }
  };

  // 🔁 Utilitaire local pour récupérer un "Date" figé Montréal à partir d'un RDV
  const getEventDate = (rdv, key /* 'start_time' | 'end_time' */) => {
    // Préfère start_utc/end_utc si dispo (prochaines routes backend)
    const val = rdv[ key === 'start_time' ? 'start_utc' : 'end_utc' ] || rdv[key];
    return toLocalDateFromMontreal(val);
  };

  const getProchainRdv = () => {
    const now = new Date();
    return [...appointments]
      .filter(r => getEventDate(r, 'start_time') > now)
      .sort((a, b) => getEventDate(a, 'start_time') - getEventDate(b, 'start_time'))[0];
  };

  // 🔁 Evénements du calendrier : on "fige" l'heure Montréal en Date local
  const events = appointments.map(rdv => ({
    id: rdv.appointment_id,
    title: `${rdv.service_name} ${isEnglish ? 'with' : 'avec'} ${rdv.professional_first_name} ${rdv.professional_last_name}`,
    start: getEventDate(rdv, 'start_time'),
    end: getEventDate(rdv, 'end_time'),
    allDay: false,
    meta: rdv
  }));

  const handleSelectEvent = (event) => {
    setSelectedRdv(event.meta);
    setModalOpen(true);
  };

  const toggleNotes = (id) => {
    setNotesVisible(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) return <p className="container-rdv">{isEnglish ? 'Loading your appointments...' : 'Chargement...'}</p>;
  if (appointments.length === 0) return <p className="container-rdv">{isEnglish ? 'You do not have any appointments yet.' : 'Vous n avez aucun rendez-vous.'}</p>;

  const prochain = getProchainRdv();

    return (
    <div className="container-rdv">
      <h2 className="rdv-header">{isEnglish ? 'My appointments' : 'Mes rendez-vous'}</h2>
      <button className="btn-primary-custom" onClick={() => navigate('/booking')}>
        {isEnglish ? '+ Book care' : '+ Prendre un rendez-vous'}
      </button>

      {prochain && (
        <div className="card-rdv">
          <h4>{isEnglish ? 'Next appointment' : 'Prochain RDV :'}</h4>
          {/* Affichage texte en heure Montréal — préfère start_utc si dispo */}
          <p>{formatInMontreal(prochain.start_utc || prochain.start_time, 'dd/MM/yyyy HH:mm')}</p>
          <button
            className="btn-primary-custom"
            onClick={() => navigate(`/patient/rdv/${prochain.appointment_id}`)}
          >
            {isEnglish ? 'View details' : 'Visualiser'}
          </button>
        </div>
      )}

      <div className="calendar-container">
        <Calendar
          localizer={localizer}
          date={currentDate}
          onNavigate={date => setCurrentDate(date)}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 700 }}
          defaultView={Views.MONTH}
          views={[Views.MONTH, Views.WEEK, Views.DAY]}
          view={currentView}
          onView={view => setCurrentView(view)}
          onSelectEvent={handleSelectEvent}
          popup
          messages={{
            month: isEnglish ? 'Month' : 'Mois',
            week: isEnglish ? 'Week' : 'Semaine',
            day: isEnglish ? 'Day' : 'Jour',
            today: isEnglish ? 'Today' : "Aujourd'hui",
            previous: isEnglish ? 'Back' : 'Precedent',
            next: isEnglish ? 'Next' : 'Suivant',
          }}
        />
      </div>

      <Modal
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        style={{
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          content: {
            position: 'static',
            inset: 'unset',
            maxWidth: '500px',
            width: '90%',
            background: 'white',
            borderRadius: '12px',
            padding: '25px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
          },
        }}
      >
        {selectedRdv && (
          <div>
            <h3>{isEnglish ? 'Appointment details' : 'Details du rendez-vous'}</h3>
            {/* Affichage texte en heure Montréal — préfère start_utc si dispo */}
            <p><strong>{isEnglish ? 'Date:' : 'Date :'}</strong> {formatInMontreal(selectedRdv.start_utc || selectedRdv.start_time, 'dd/MM/yyyy HH:mm:ss')}</p>
            <p><strong>{isEnglish ? 'With:' : 'Avec :'}</strong> {selectedRdv.professional_first_name} {selectedRdv.professional_last_name}</p>
            <p><strong>{isEnglish ? 'Service:' : 'Service :'}</strong> {selectedRdv.service_name}</p>
            <p><strong>{isEnglish ? 'Status:' : 'Statut :'}</strong> {formatCareLabel(selectedRdv.status, isEnglish ? 'en' : 'fr')}</p>

            <button onClick={() => toggleNotes(selectedRdv.appointment_id)}>
              {notesVisible[selectedRdv.appointment_id] ? (isEnglish ? 'Hide notes' : 'Masquer les notes') : (isEnglish ? 'View notes' : 'Voir les notes')}
            </button>

            {notesVisible[selectedRdv.appointment_id] && selectedRdv.notes && (
              <div className="notes-section">
                {selectedRdv.notes.length > 0 ? (
                  <ul>
                    {selectedRdv.notes.map(note => (
                      <li key={note.note_id}>
                        {formatInMontreal(note.created_at, 'dd/MM/yyyy HH:mm')} — {note.author_first_name} {note.author_last_name} :<br />
                        <em>{note.content}</em>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p><em>{isEnglish ? 'No notes are available for this appointment.' : 'Aucune note disponible pour ce rendez-vous.'}</em></p>
                )}
              </div>
            )}
            <div className="modal-actions">
              <button onClick={() => setModalOpen(false)} className="btn-secondary">{isEnglish ? 'Close' : 'Fermer'}</button>
              <button className="btn-danger-custom" onClick={() => handleAnnuler(selectedRdv.appointment_id)}>
                {isEnglish ? 'Cancel appointment' : 'Annuler le rendez-vous'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default MesRendezvous;
